import { useSessionStore } from '@/store/sessionStore';
import { cmd } from '@/session/sessionRuntime';
import { useCountdown, formatMs } from '@/hooks/useCountdown';
import { INDICATOR_LABEL, type Indicator } from '@/protocol/types';

export function PlayingView(): JSX.Element {
  const self = useSessionStore((s) => s.self);
  const players = useSessionStore((s) => s.players);
  const deadline = useSessionStore((s) => s.phaseDeadlineAt);
  const word = useSessionStore((s) => s.word);
  const swipeCount = useSessionStore((s) => s.swipeCount);
  const remaining = useCountdown(deadline);

  const isChief = self?.isChief ?? false;
  const isWolf = self?.baseRole === 'wolf' || self?.role === 'wolf';
  const isSeer = self?.baseRole === 'seer' || self?.role === 'seer';
  const knowsWord = isChief || isWolf || isSeer;

  // 占位 swipe — 真实手势 D3 接入
  const placeholderSwipe = (indicator: Indicator): void => {
    cmd.swipe({ indicator });
  };

  return (
    <main
      data-testid="playing-view"
      className="mx-auto flex max-w-md flex-col gap-4 p-4"
    >
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-pig-700">游戏进行中</h1>
        <div
          data-testid="playing-countdown"
          className="text-sm font-mono text-gray-600"
        >
          {formatMs(remaining)}
        </div>
      </div>

      {knowsWord && (
        <section
          data-testid="word-display"
          className="rounded-lg bg-amber-50 p-3 text-center"
        >
          <div className="text-xs text-amber-800">本局词</div>
          <div className="mt-1 text-2xl font-bold text-amber-900">{word ?? '—'}</div>
        </section>
      )}

      {!isChief ? (
        <>
          <section className="rounded-lg bg-white p-4 shadow">
            <div className="text-base font-semibold text-pig-700">请向村长口头提问</div>
            <div className="mt-2 text-sm text-gray-600">
              通过 是/否/也许/接近/不相关/猜对了 引导你的推理。
            </div>
          </section>
          <section
            data-testid="player-list"
            className="rounded-lg bg-white p-3 shadow"
          >
            <h2 className="mb-2 text-sm font-semibold text-gray-600">本局玩家</h2>
            <ul className="space-y-1">
              {players
                .filter((p) => p.inMatch || p.online)
                .map((p) => (
                  <li
                    key={p.token}
                    className="flex items-center justify-between rounded border border-gray-100 px-3 py-2"
                  >
                    <span>{p.nickname}</span>
                    {p.isChief && (
                      <span className="rounded bg-amber-100 px-1 text-xs">村长</span>
                    )}
                  </li>
                ))}
            </ul>
          </section>
        </>
      ) : (
        <>
          <section className="rounded-lg bg-white p-4 shadow">
            <div className="text-base font-semibold text-pig-700">
              横屏后用三个标志判定
            </div>
            <div className="mt-1 text-sm text-gray-600">
              （真实横屏手势在 D3 接入；本单为占位 UI）
            </div>
          </section>

          <section
            data-testid="chief-swipe-placeholder"
            className="grid grid-cols-3 gap-3"
          >
            <button
              data-testid="swipe-yes"
              onClick={() => placeholderSwipe('yes')}
              className="h-24 rounded-lg bg-green-500 text-3xl font-bold text-white shadow"
              aria-label={INDICATOR_LABEL.yes}
            >
              ✓
            </button>
            <button
              data-testid="swipe-no"
              onClick={() => placeholderSwipe('no')}
              className="h-24 rounded-lg bg-rose-500 text-3xl font-bold text-white shadow"
              aria-label={INDICATOR_LABEL.no}
            >
              ✗
            </button>
            <button
              data-testid="swipe-maybe"
              onClick={() => placeholderSwipe('maybe')}
              className="h-24 rounded-lg bg-amber-400 text-3xl font-bold text-white shadow"
              aria-label={INDICATOR_LABEL.maybe}
            >
              ?
            </button>
          </section>

          <div
            data-testid="swipe-count"
            className="text-center text-xs text-gray-500"
          >
            已判定 {swipeCount} 次
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="end-game-good-win-btn"
              onClick={() => cmd.endGame('good_win')}
              className="h-12 rounded-lg bg-emerald-600 text-base font-semibold text-white shadow"
            >
              好人赢
            </button>
            <button
              data-testid="end-game-wolf-win-btn"
              onClick={() => cmd.endGame('wolf_win')}
              className="h-12 rounded-lg bg-rose-600 text-base font-semibold text-white shadow"
            >
              狼赢
            </button>
          </div>
        </>
      )}
    </main>
  );
}
